import axios from "axios";
import { signInWithCustomToken, signOut, GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { getFirebaseAuth, isFirebaseConfigured } from "../config/firebase";
import { skitude_api_url, skitude_api_key, skitude_app_token } from "../../src-dynamic/destination.sso.js";

/**
 * Login with email/password. Calls auth API to get Firebase custom token, then signs in.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{ user: import('firebase/auth').User }>}
 */
export async function loginWithFirebase(email, password) {
  if (!isFirebaseConfigured()) {
    throw new Error("Firebase is not configured");
  }

  const response = await axios.post(
    `${skitude_api_url}/v4/auth/login`,
    { email, password },
    {
      headers: {
        "x-api-key": skitude_api_key,
        "application-token": skitude_app_token,
      },
    }
  );

  const jwt = response.data?.object?.jwt

  if (!jwt) {
    throw new Error("Wrong email or password");
  }
  const auth = getFirebaseAuth();
  const userCredential = await signInWithCustomToken(auth, jwt);
  return { user: userCredential.user };
}

/**
 * Login with Google via Firebase popup.
 * @returns {Promise<{ user: import('firebase/auth').User }>}
 */
export async function loginWithGoogle() {
  if (!isFirebaseConfigured()) {
    throw new Error("Firebase is not configured");
  }

  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  const userCredential = await signInWithPopup(auth, provider);
  return { user: userCredential.user };
}

/**
 * Login with Apple via Firebase popup.
 * @returns {Promise<{ user: import('firebase/auth').User }>}
 */
export async function loginWithApple() {
  if (!isFirebaseConfigured()) {
    throw new Error("Firebase is not configured");
  }

  const auth = getFirebaseAuth();
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  const userCredential = await signInWithPopup(auth, provider);
  return { user: userCredential.user };
}

/**
 * Logout from Firebase.
 */
export async function logoutFirebase() {
  if (!isFirebaseConfigured()) return;

  const auth = getFirebaseAuth();
  if (auth?.currentUser) {
    await signOut(auth);
  }
}

/**
 * Check if user is logged in (Firebase user exists and is not anonymous).
 * @returns {Promise<boolean>}
 */
export async function isLoggedInFirebase() {
  if (!isFirebaseConfigured()) return false;
  const auth = getFirebaseAuth();
  return new Promise((resolve) => {
    const unsub = auth.onAuthStateChanged((user) => {
      unsub();
      resolve(Boolean(user && !user.isAnonymous));
    });
  });
}

export { isFirebaseConfigured };
